import classnames from "classnames";
import { useCaptureEventsV2 } from "@gx1/gx-npm-lib";
import { IconButton, Loader, TooltipV2, TypographyComponent } from "@gx1/gx-npm-ui";
import { Fragment, useContext, useEffect, useState, useRef, useCallback, forwardRef, ForwardedRef } from "react";
import { useTranslation } from "react-i18next";
import { navigateToUrl } from "single-spa";
import { ClientEvent, InitUserTeam, NotificationDiscussionSource, UserNotificationType } from "../../../app.constants";
import { MainNavAppV2Context } from "../../../app-v2.context";
import { UserNotification } from "../../../app.types";
import { DRAWER_TRANSITION_DURATION } from "../notifications.constants";
import { NotificationRowDiscussionComponent } from "./notification-row/notification-row-discussion.component";
import { BackToTopIcon } from "./notifications-back-to-top.icon";
import styles from "./notifications-container.styles.module.scss";

type LoadNotificationResDto = { lastEvalKey: string; notifications: UserNotification[]; };
const FOOTER_HEIGHT_PX = 48;

const NotificationsContainerComponent = forwardRef<HTMLDivElement>((_props, parentRef: ForwardedRef<HTMLDivElement>) => {
  const { t } = useTranslation();
  const { notifications, setNotifications, setIsNotificationsPanelOpen } = useContext(MainNavAppV2Context);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextPageKey, setNextPageKey] = useState<string>("");
  const captureEvents = useCaptureEventsV2();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const currentParentRef = parentRef && "current" in parentRef ? parentRef.current : null;
  const currentScrollHeight = currentParentRef ? currentParentRef.scrollHeight : 0;
  const currentClientHeight = currentParentRef ? currentParentRef.clientHeight : 0;
  const scrollToTop = () => {
    if (currentParentRef) {
      currentParentRef.scrollTo({ behavior: "smooth", top: 0 });
    }
  };

  const onDiscussionNotificationClick = (notification: UserNotification) => {
    setIsNotificationsPanelOpen(false);
    const tab = {
      [NotificationDiscussionSource.REQUIREMENT]: "requirements",
      [NotificationDiscussionSource.QUESTIONNAIRE]: "questionnaires",
      [NotificationDiscussionSource.SCORECARD]: "scorecard",
      [NotificationDiscussionSource.QUESTIONNAIRE_CROSS_TEAM]: "questionnaires",
    }[notification.discussionSource];
    captureEvents([
      {
        eventType: ClientEvent.NOTIFICATIONS_FEED_NOTIFICATION_CLICKED,
        metaData: { notificationType: notification.notificationType, discussionId: notification.discussionId },
      },
    ]);

    setTimeout(() => {
      fetch(`${window.gxApiConnection}/api/v2/user/notifications/discussion-read`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ discussionId: notification.discussionId }),
      });
      let url: string;

      if (notification.initUserTeam === InitUserTeam.SURVEY_RECIPIENT &&
        notification.discussionSource === NotificationDiscussionSource.QUESTIONNAIRE_CROSS_TEAM &&
        notification.surveyId) {
        url = `/s/tasks/${notification.initiativeId}/questionnaire/${notification.surveyId}/product/${notification.initProductId}/responses/discussion/${notification.discussionId}?gxtm_source=buysmart&gxtm_type=notifications`;
      } else if (notification.discussionSource === NotificationDiscussionSource.QUESTIONNAIRE_CROSS_TEAM && notification.surveyId) {
        url = `/s/evaluation/${notification.initiativeId}/rounds/${tab}/${notification.surveyId}/discussion/${notification.discussionId}?tab=recipient&gxtm_source=buysmart&gxtm_type=notifications`;
      } else if (notification.discussionSource === NotificationDiscussionSource.QUESTIONNAIRE && notification.surveyId) {
        url = `/s/evaluation/${notification.initiativeId}/rounds/${tab}/${notification.surveyId}/discussion/${notification.discussionId}?tab=evaluator&gxtm_source=buysmart&gxtm_type=notifications`;
      } else {
        url = `/s/evaluation/${notification.initiativeId}/rounds/${tab}/discussion/${notification.discussionId}?gxtm_source=buysmart&gxtm_type=notifications`;
      }

      navigateToUrl(url);
    }, DRAWER_TRANSITION_DURATION);
  };

  const loadNotifications = useCallback(
    async (lastEvalKey = ""): Promise<boolean> => {
      let nextLastEvalKey = "";
      try {
        const response = await fetch(`${window.gxApiConnection}/api/v2/user/notifications/load`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ lastEvalKey }),
        });
        if (response.ok) {
          const { data }: { data: LoadNotificationResDto } = await response.json();
          setNotifications((prevNotifications) => [...prevNotifications, ...data.notifications]);
          setNextPageKey(data.lastEvalKey);
          nextLastEvalKey = data.lastEvalKey;
        }
      } catch (err) {}
      return nextLastEvalKey !== "";
    },
    [setNotifications]
  );

  useEffect(() => {
    setIsLoading(true);
    void (async () => {
      await loadNotifications();
      setIsLoading(false);
    })();

    return () => {
      setNotifications([]);
      setNextPageKey("");
    };
  }, [loadNotifications, setNotifications]);

  useEffect(() => {
    if (!nextPageKey || isLoadingMore) {
      return;
    }
    const observer = new IntersectionObserver(async (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        setIsLoadingMore(true);
        const hasMore = await loadNotifications(nextPageKey);
        setIsLoadingMore(false);
        if (!hasMore) {
          observer.disconnect();
        }
      }
    });
    const elementToObserve = loadMoreRef.current;
    if (elementToObserve) {
      observer.observe(elementToObserve);
    }
    return () => {
      if (elementToObserve) {
        observer.unobserve(elementToObserve);
      }
    };
  }, [nextPageKey, loadNotifications, isLoadingMore]);

  return (
    <div className={styles.root}>
      {notifications.map((x, index) => {
        switch (x.notificationType) {
            case UserNotificationType.DISCUSSION_ACTIVITY:
            case UserNotificationType.DISCUSSION_COMMENT_MENTION:
              return (
                <div
                  className={styles.listItem}
                  key={x.notificationId}
                  role="link"
                  aria-label={`notification ${index + 1}`}
                  tabIndex={0}
                  onClick={() => onDiscussionNotificationClick(x)}
                  onKeyDown={(e) => e.key === "Enter" && onDiscussionNotificationClick(x)}
                >
                  <NotificationRowDiscussionComponent notification={x} />
                </div>
              );
            default:
              return null;
        }
      })}
      <div
        ref={loadMoreRef}
        style={{ height: "1px", opacity: 0 }}
        aria-hidden="true"
        data-testid="load-more-trigger"
      />
      {(isLoading || isLoadingMore) &&
        <Loader rootClassName={classnames(styles.loader, isLoadingMore && styles.isLoadingMore)} />
      }
      {nextPageKey === "" &&
        notifications.length > 0 &&
        currentScrollHeight > currentClientHeight + FOOTER_HEIGHT_PX && (
        <Fragment>
          <div className={styles.endOfListDivider}>
            <div className={styles.solidLine} />
            <TypographyComponent rootClassName={styles.endOfText} styling="p5" color="iron">
              {t("End of notifications")}
            </TypographyComponent>
            <div className={styles.solidLine} />
          </div>
          <div className={styles.endOfListButtonContainer}>
            <TooltipV2
              title={t("Back to the top")}
              placement="top"
              PopperProps={{
                modifiers: [
                  { name: "offset", options: { offset: [0, 0] } },
                ]
              }}
            >
              <div>
                <IconButton
                  ariaLabel={t("Back to the top")}
                  width={24}
                  height={24}
                  borderRadius="50%"
                  onClick={scrollToTop}
                >
                  <BackToTopIcon />
                </IconButton>
              </div>
            </TooltipV2>
          </div>
        </Fragment>
      )}
    </div>
  );
});

export default NotificationsContainerComponent;
